
import React, { useEffect } from 'react';

interface StarterInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const StarterInfoModal: React.FC<StarterInfoModalProps> = ({ isOpen, onClose }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 md:p-8 border-b border-slate-100">
          <div>
            <h3 className="text-2xl font-black text-slate-900">STARTER</h3>
            <p className="text-slate-500 text-sm font-bold mt-1">Service Breakdown</p>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900 transition-colors"
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto p-6 md:p-8 space-y-8 text-slate-600 leading-relaxed text-sm md:text-base">
          
          <section>
            <h4 className="text-blue-600 font-black text-lg mb-2 flex items-center gap-2">
              <i className="fas fa-globe"></i> Websites
            </h4>
            <p className="font-medium text-slate-900 mb-3">
              Your main online presence where people learn about your business or community.
            </p>
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">In plain terms</span>
                <p className="text-slate-700 font-semibold leading-relaxed">A modern, professional website that represents you clearly.</p>
            </div>
          </section>

          <hr className="border-slate-100" />

          <section>
            <h4 className="text-blue-600 font-black text-lg mb-2 flex items-center gap-2">
              <i className="fas fa-pen-nib"></i> Blogs
            </h4>
            <p className="font-medium text-slate-900 mb-3">
              A simple way to share updates, stories, articles, or announcements.
            </p>
             <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">In plain terms</span>
                <p className="text-slate-700 font-semibold leading-relaxed">Easy publishing so you can keep your site fresh and relevant.</p>
            </div>
          </section>

          <hr className="border-slate-100" />

          <section>
            <h4 className="text-blue-600 font-black text-lg mb-2 flex items-center gap-2">
              <i className="fas fa-calendar-alt"></i> Events
            </h4>
            <p className="font-medium text-slate-900 mb-3">
              Pages and tools to promote and manage events, classes, or gatherings.
            </p>
             <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">In plain terms</span>
                <p className="text-slate-700 font-semibold leading-relaxed">One place to share event details and manage registrations.</p>
            </div>
          </section>

          <hr className="border-slate-100" />

          <section>
            <h4 className="text-blue-600 font-black text-lg mb-2 flex items-center gap-2">
              <i className="fas fa-tools"></i> Basic Business Apps
            </h4>
            <p className="font-medium text-slate-900 mb-3">
              Simple web-based tools built to help you manage day-to-day tasks.
            </p>
            <div className="mb-4 pl-4 border-l-2 border-blue-100">
                <span className="text-xs font-bold text-slate-500 block mb-2">Examples:</span>
                <ul className="space-y-1.5 font-medium text-slate-700">
                    <li className="flex items-center gap-2"><i className="fas fa-circle text-[6px] text-blue-400"></i> Managing content or members</li>
                    <li className="flex items-center gap-2"><i className="fas fa-circle text-[6px] text-blue-400"></i> Handling enquiries</li>
                    <li className="flex items-center gap-2"><i className="fas fa-circle text-[6px] text-blue-400"></i> Tracking bookings or updates</li>
                </ul>
            </div>
             <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">In plain terms</span>
                <p className="text-slate-700 font-semibold leading-relaxed">Small apps that make running your business easier.</p>
            </div>
          </section>

        </div>

        {/* Footer Actions */}
        <div className="p-6 md:p-8 border-t border-slate-100 bg-slate-50 rounded-b-3xl flex justify-end">
          <button 
            onClick={onClose}
            className="px-8 py-3 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/10"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StarterInfoModal;
